import {
  format,
  differenceInYears,
  differenceInHours,
  differenceInMonths,
  formatDistanceToNow,
  parseISO,
  parse,
  addDays,
} from 'date-fns';
import type { Locale } from 'date-fns';
import it from 'date-fns/locale/it';
import enGB from 'date-fns/locale/en-GB';

export const dateFormat = `dd MMM yyyy`;
export const formatDate = (date: Date | string): string => format(new Date(date), dateFormat, { locale: it });

/**
 * date-fns locale for a next-i18next locale code.
 *
 * Italian is the fallback because it is the app's `defaultLocale`: an unknown
 * code must not silently drop to date-fns' built-in en-US, which is how the
 * application timeline ended up printing one Italian and one English date in
 * the same sentence.
 */
export const getDateFnsLocale = (locale?: string): Locale => (locale?.startsWith('en') ? enGB : it);

/** `formatDate` in the reader's language. `formatDate` itself stays Italian-only: it has callers well outside any localised screen. */
export const formatDateIn = (date: Date | string, locale?: string, dateFormatString?: string): string =>
  format(new Date(date), dateFormatString || dateFormat, { locale: getDateFnsLocale(locale) });

export const toDateFormat = (date: Date | string, newDateFormat?: string) =>
  format(new Date(date), newDateFormat || dateFormat, { locale: it });
export const genericDateFormat = 'yyyy-MM-dd';

export const getYearsElapsed = (date: Date | string) => {
  const today = new Date();
  const birthDate = new Date(date);
  return differenceInYears(today, birthDate);
};

export const getMonthsElapsed = (date: Date | string) => {
  const today = new Date();
  const comparison = new Date(date);
  return differenceInMonths(today, comparison);
};

export const getHoursElapsed = (date: Date | string) => {
  const today = new Date();
  const comparison = new Date(date);
  return differenceInHours(today, comparison);
};

export const getTimeElapsed = (date: Date | string) => {
  return formatDistanceToNow(new Date(date), { locale: it });
};
export const formatDateTo = (date: Date | string, dateFormatString: string): string =>
  format(new Date(date), dateFormatString, { locale: it });

/**
 * Formats an ISO date string into 'dd/MM/yy' format.
 *
 * @param {string} [dateStr] - The ISO date string to format.
 * @returns {string} - The formatted date, or an empty string if input is invalid.
 */
export function formatDateToDayMonthYear(dateStr?: string): string {
  if (!dateStr) return '';
  const date = parseISO(dateStr);
  return format(date, 'dd/MM/yy');
}

export function addDaysToDayMonthYear(dateStr?: string, daysToAdd = 1) {
  if (!dateStr) return '';
  const parsedDate = parse(dateStr, 'dd/MM/yy', new Date());
  const dateWithAddition = addDays(parsedDate, daysToAdd);
  return format(dateWithAddition, 'dd/MM/yy');
}
